import {
  resolvePositionAssignments,
  type ActivatedUserPayload,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { DEFAULT_ICS_FORM_FAMILY } from 'components/IAP/formFamily';
import {
  defaultDraftSeats,
  rosterNodesForCreate,
} from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import type { Incident } from 'components/IAP/hooks/incidentHooks';

/** Stable Hub name for the FOC 7.2 staffed Full ICS demo workspace. */
export const FOC_DEMO_INCIDENT_ROSTER_NAME = 'FOC Demo — Incident Roster';

/** Keep in sync with FOC_INCIDENT_ROSTER_TOUR_PARAM (avoid circular import). */
const FOC_INCIDENT_ROSTER_TOUR_PARAM = 'focIncidentRosterTour';

type NamedIncident = {
  id: string;
  name?: string | null;
  archived?: number | boolean;
};

export function findFocIncidentRosterDemo(
  incidents: readonly NamedIncident[] = []
): NamedIncident | null {
  const demo = incidents.find(
    (incident) =>
      !incident.archived &&
      String(incident.name ?? '').trim() === FOC_DEMO_INCIDENT_ROSTER_NAME
  );
  return demo ?? null;
}

/**
 * Preference for 7.2 Hub resolve:
 * 1) named FOC demo, 2) current live, 3) first live.
 */
export function preferredFocIncidentRosterId(
  incidents: readonly NamedIncident[] = [],
  currentId?: string | null
): string | null {
  const demo = findFocIncidentRosterDemo(incidents);
  if (demo) return String(demo.id);

  if (currentId) {
    const current = incidents.find(
      (incident) =>
        String(incident.id) === String(currentId) && !incident.archived
    );
    if (current) return String(current.id);
    return String(currentId);
  }

  const live = incidents.find((incident) => !incident.archived);
  return live ? String(live.id) : null;
}

export type EnsureFocIncidentRosterDemoDeps = {
  incidents: readonly NamedIncident[];
  groupGuid: string;
  user: {
    user_guid: string;
    name?: string | null;
    email_address?: string | null;
  };
  createIncident: (payload: {
    name: string;
    group_guid: string;
    areas_of_responsibility: string[];
    incident_types: string[];
    ics_form_family: string;
    activated_users: ActivatedUserPayload[];
    roster_nodes: ReturnType<typeof rosterNodesForCreate>;
  }) => Promise<Pick<Incident, 'id' | 'name'>>;
};

/**
 * Returns the demo incident id, creating a Full ICS workspace with the current
 * user seated as Incident Commander when the named demo is missing.
 */
export async function ensureFocIncidentRosterDemoWorkspace(
  deps: EnsureFocIncidentRosterDemoDeps
): Promise<string | null> {
  const existing = findFocIncidentRosterDemo(deps.incidents);
  if (existing) return String(existing.id);

  if (!deps.groupGuid || !deps.user.user_guid) return null;

  const seats = defaultDraftSeats();
  const activatedUser: ActivatedUserPayload = {
    user_guid: deps.user.user_guid,
    role: 'Incident Commander',
    email_address: deps.user.email_address || '',
    name: deps.user.name || deps.user.email_address || 'FOC Demo User',
  };

  const created = await deps.createIncident({
    name: FOC_DEMO_INCIDENT_ROSTER_NAME,
    group_guid: deps.groupGuid,
    areas_of_responsibility: [],
    // Avoid Exercise so applyExerciseIncidentName does not rewrite the demo title.
    incident_types: ['Training'],
    ics_form_family: DEFAULT_ICS_FORM_FAMILY,
    activated_users: [activatedUser],
    roster_nodes: rosterNodesForCreate(seats),
  });

  const incidentId = created?.id ? String(created.id) : null;
  if (!incidentId) return null;

  try {
    // Dynamic import keeps resolve/find helpers free of the roster API graph.
    const { addUsersToIncidentRoster, getPositionsForIncident } = await import(
      'hooks/incident/useRoster'
    );
    const positions = await getPositionsForIncident(incidentId);
    const positionAssignments = resolvePositionAssignments(
      [
        {
          user_guid: deps.user.user_guid,
          name: activatedUser.name,
          email_address: activatedUser.email_address,
          position: 'Incident Commander',
        },
      ],
      positions
    );
    if (positionAssignments.length > 0) {
      await addUsersToIncidentRoster({
        incidentId,
        groupGuid: deps.groupGuid,
        positionAssignments,
      });
    }
  } catch {
    // Creator is still an activated member; seats can be assigned manually.
  }

  return incidentId;
}

export function focIncidentRosterDemoHref(incidentId: string): string {
  return `/iap/${incidentId}/roster?${FOC_INCIDENT_ROSTER_TOUR_PARAM}=1`;
}
